/**
 * Create / edit a project.
 *
 * The reference implementation of the CoOrdio form pattern in Cortex. Other forms should follow this
 * shape: a Zod schema as the single source of validation, React Hook Form for state, one
 * `FormField` per input wrapping an `Rhf*Field` bridge, `FormErrors` for the summary, `FormActions`
 * for the buttons.
 *
 * WHY the bridges rather than plain inputs: each `Rhf*Field` emits `data-pw` (the field's RHF name)
 * and `data-field-type` (one of 13 control kinds — `text`, `textarea`, `checkbox`, `date`, `select`,
 * `masked`, …). That pair is the contract CoordioCore's generic form engine drives forms through:
 * it discovers every field, fills it with a type-appropriate value, reads it back and diffs. A form
 * built from raw inputs is invisible to that engine, so it has to be tested by hand, field by field.
 * Cortex gets the contract for free by using the bridges — nothing here has to emit the attributes.
 *
 * The bridge is a direct child of `FormField`, which passes ids and ARIA down through context — so
 * no `fieldSlot` render-prop boilerplate is needed for these controls.
 */

import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormActions,
  FormErrors,
  FormField,
  RhfInputField,
  RhfTextareaField,
  Switch,
  rhfFieldErrorMessages,
} from '@coordio/ui';
import { FormSection } from '@coordio/ui/layout';

import { projectService } from '../../services/projectService';
import { useContextStore } from '../../store/contextStore';
import { useNotificationStore } from '../../store/notificationStore';

import type { CreateProjectRequest, ProjectDto, UpdateProjectRequest } from '../../types/project';

/**
 * Slugs are URL path segments, so the shape is enforced rather than merely suggested — an invalid
 * slug produces a project that cannot be linked to, which is not visible at the point of typing.
 */
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

const projectSchema = z.object({
  name: z.string().trim().min(1, 'Name is required.').max(200, 'Name must be 200 characters or fewer.'),
  slug: z
    .string()
    .trim()
    .min(1, 'Slug is required.')
    .max(100, 'Slug must be 100 characters or fewer.')
    .regex(SLUG_PATTERN, 'Use lowercase letters, numbers and single hyphens (e.g. claims-triage).'),
  description: z.string().trim().max(1000, 'Description must be 1000 characters or fewer.').optional(),
  isActive: z.boolean(),
});

type ProjectFormValues = z.infer<typeof projectSchema>;

/** Field name → the label shown above its input, so the error summary matches the form. */
const FIELD_LABELS: Partial<Record<keyof ProjectFormValues, string>> = {
  name: 'Name',
  slug: 'Slug',
  description: 'Description',
  isActive: 'Active',
};

/**
 * `rhfFieldErrorMessages` includes the `root` key, and `FormErrors` renders `rootError` in its own
 * slot — so passing the raw map alongside `rootError` prints a server failure twice, once as a
 * banner and once as a phantom field named "root". Dropping it here keeps each message in one place.
 */
function fieldErrorsWithoutRoot(messages: Record<string, string | undefined>) {
  const { root: _root, ...fieldErrors } = messages;
  return fieldErrors;
}

/** Mirrors the slug rule above so a generated value always passes its own validation. */
function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function toFormValues(project: ProjectDto | null): ProjectFormValues {
  return {
    name: project?.name ?? '',
    slug: project?.slug ?? '',
    description: project?.description ?? '',
    isActive: project?.isActive ?? true,
  };
}

interface ProjectFormProps {
  project: ProjectDto | null;
  open: boolean;
  onClose: () => void;
}

export const ProjectForm = ({ project, open, onClose }: ProjectFormProps) => {
  const currentTenant = useContextStore((s) => s.currentTenant);
  const { showNotification } = useNotificationStore();

  const isEdit = project !== null;

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    setError,
    watch,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<ProjectFormValues>({
    resolver: zodResolver(projectSchema),
    defaultValues: toFormValues(project),
    mode: 'onBlur',
  });

  // Reopening the dialog for a different record must not show the previous one's values.
  useEffect(() => {
    reset(toFormValues(project));
  }, [project, open, reset]);

  /**
   * Derive the slug from the name while creating — but stop as soon as the user edits the slug
   * themselves, and never touch it when editing an existing project: the slug is part of the
   * project's URL, so silently rewriting it would break existing links.
   */
  const nameValue = watch('name');
  useEffect(() => {
    if (isEdit || dirtyFields.slug) return;
    setValue('slug', slugify(nameValue ?? ''), { shouldValidate: false });
  }, [nameValue, isEdit, dirtyFields.slug, setValue]);

  const onSubmit = handleSubmit(async (values) => {
    if (!currentTenant) return;

    try {
      if (project) {
        const payload: UpdateProjectRequest = {
          name: values.name,
          slug: values.slug,
          description: values.description || undefined,
          isActive: values.isActive,
        };
        await projectService.updateProject(project.id, currentTenant.id, payload);
        showNotification('Project updated successfully', 'success');
      } else {
        const payload: CreateProjectRequest = {
          name: values.name,
          slug: values.slug || undefined,
          description: values.description || undefined,
        };
        await projectService.createProject(currentTenant.id, payload);
        showNotification('Project created successfully', 'success');
      }
      onClose();
    } catch (err) {
      // Surface the failure on the form via `root`, not only as a toast: a toast that has already
      // faded leaves the user staring at a form that looks fine and did not save.
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to save project.';
      setError('root', { message });
      showNotification(message, 'error');
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="project-form max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit project' : 'New project'}</DialogTitle>
        </DialogHeader>

        {!currentTenant ? (
          <p className="project-form__guard">Select a workspace before creating a project.</p>
        ) : (
          <form onSubmit={onSubmit} noValidate>
            <FormErrors
              rootError={errors.root?.message}
              fieldErrors={fieldErrorsWithoutRoot(rhfFieldErrorMessages(errors))}
              fieldLabels={FIELD_LABELS}
              autoNavigateToFirstError
            />

            <FormSection title="Project details" description="Name, slug and description for the project.">
              <div className="grid gap-5 items-end sm:grid-cols-2">
                <FormField label="Name" required error={errors.name?.message} data-testid="project-name">
                  <RhfInputField control={control} name="name" placeholder="Claims triage" autoFocus />
                </FormField>

                <FormField
                  label="Slug"
                  required
                  description="URL-friendly identifier. Generated from the name until you edit it."
                  error={errors.slug?.message}
                  data-testid="project-slug"
                >
                  <RhfInputField control={control} name="slug" placeholder="claims-triage" />
                </FormField>
              </div>

              <div className="mt-5">
                <FormField label="Description" error={errors.description?.message} data-testid="project-description">
                  <RhfTextareaField control={control} name="description" rows={3} />
                </FormField>
              </div>

              {isEdit && (
                <div className="mt-5">
                  <div className="ui-form-choice-field">
                    <div className="ui-form-choice-field__row">
                      <div className="ui-form-choice-field__control">
                        <Controller
                          control={control}
                          name="isActive"
                          render={({ field }) => (
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              data-testid="project-isActive-switch"
                            />
                          )}
                        />
                      </div>
                      <div className="ui-form-choice-field__label-cell">
                        <span className="ui-form-choice-field__label">Active</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </FormSection>

            <DialogFooter>
              <FormActions>
                <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="default" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving…' : isEdit ? 'Save changes' : 'Create project'}
                </Button>
              </FormActions>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
